import React, { Suspense, lazy } from 'react';
import './App.css';
import "../node_modules/bootstrap/dist/css/bootstrap.min.css";
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Spinner from 'react-bootstrap/Spinner';
import Navigbar from './components/Navbar';
import Intro from './pages/intro';
import NotFound from './pages/notfound';
import MainWrapper from './layouts/MainWrapper';
// Login/Register/Logout stay statically imported: Navbar already imports the
// same form components for its modals, so they live in the main chunk anyway.
import Login from './views/login';
import Logout from './views/logout';
import Register from './views/register';

// Route-level code splitting: everything below is loaded on demand so the
// landing page doesn't pay for the calculator (and its chart.js dependency
// tree) up front. Each lazy page becomes its own chunk served by WhiteNoise
// with hashed filenames, so caching is unaffected.
const Calculator = lazy(() => import('./pages/calculator.js'));
const Ladder = lazy(() => import('./pages/ladder.js'));
const Methodology = lazy(() => import('./pages/methodology'));
const Returns = lazy(() => import('./pages/returns'));
const ForwardReturns = lazy(() => import('./pages/forwardreturns'));
const WithdrawalStrategies = lazy(() => import('./pages/withdrawalstrategies'));
const Research = lazy(() => import('./pages/academicresearch'));
const Terms = lazy(() => import('./pages/terms'));
const Privacy = lazy(() => import('./pages/privacy'));
const PlanRisks = lazy(() => import('./pages/planrisks.js'));
const About = lazy(() => import('./pages/about'));
const WhoIsItFor = lazy(() => import('./pages/whoisitfor.js'));
const QuickTest = lazy(() => import('./pages/quicktest.js'));
const Links = lazy(() => import('./pages/links.js'));
const InheritanceTax = lazy(() => import('./pages/inheritancetax'));
const ResetPassword = lazy(() => import('./components/resetpassword'));
const EmailVerification = lazy(() => import('./pages/emailverification'));

// Shown briefly while a lazy route chunk downloads. Matches the app's dark
// page background so there's no white flash.
const RouteLoading = () => (
  <div
    style={{
      minHeight: '100vh',
      background: '#0f1114',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Spinner animation="border" variant="info" role="status" aria-label="Loading page" />
  </div>
);

function App() {

	const helmetContext = {};

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <HelmetProvider context={helmetContext}>
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <MainWrapper>
      <Navigbar />
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path='/' element={<Intro />}></Route>
        <Route path='/quicktest' element={<QuickTest />}></Route>
        <Route path='/calc' element={<Calculator />}></Route>
        <Route path='/decumulation' element={<Calculator />}></Route>
        {/* Accumulation folded into Plan Simulator as an in-page Full/
            Simplified toggle — redirect rather than 404, for any existing
            bookmarks/backlinks/indexed search results. */}
        <Route path='/accumulation' element={<Navigate to="/calc" replace />} />
        <Route path='/ladder' element={<Ladder />}></Route>
        <Route path='/withdrawal' element={<WithdrawalStrategies />}></Route>
        <Route path='/returns' element={<Returns />}></Route>
        <Route path='/forwardreturns' element={<ForwardReturns />}></Route>
        <Route path='/planrisk' element={<PlanRisks />}></Route>
        <Route path='/research' element={<Research />}></Route>
        <Route path='/inheritancetax' element={<InheritanceTax />}></Route>
        <Route path='/links' element={<Links />}></Route>
        {/* Strategy Comparison Calculator folded into the Decumulation
            Strategies (research) page — redirect rather than 404, for any
            existing bookmarks/backlinks/indexed search results. */}
        <Route path='/strategycomparisoncalc' element={<Navigate to="/research" replace />} />
        <Route path='/methodology' element={<Methodology />}></Route>
        <Route path='/terms' element={<Terms />}></Route>
        <Route path='/privacy' element={<Privacy />}></Route>
        <Route path='/about' element={<About />}></Route>
        <Route path='/whoisitfor' element={<WhoIsItFor />}></Route>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/reset-password/:uidb64/:token" element={<ResetPassword />} />
        <Route path="/activate/:uidb64/:token" element={<EmailVerification />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </MainWrapper>
    </Router>
    </HelmetProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
